import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { toSignal } from '@angular/core/rxjs-interop'
import { filter, map, startWith } from 'rxjs'
import { AgencyService } from '../services/agency.service'

@Component({
  selector: 'side-nav',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <aside class="text-white px-2 w-44 flex-shrink-0 overflow-y-auto">
      <nav class="py-2">
        <div class="mb-6">
          <ul class="space-y-2">
            @for (item of menuItems(); track item.label) {
              <li>
                <a
                  class="flex px-4 py-2 rounded hover:bg-[#151829] transition-colors cursor-pointer"
                  [class.active-link]="isActiveRoute(item.link)"
                  (click)="navigate(item.link)"
                >
                  <i class="material-icons mr-3">{{ item.icon }}</i>
                  <span>{{ item.label }}</span>
                </a>
              </li>
            }
          </ul>
        </div>
      </nav>
    </aside>
  `,
  styles: [
    `
      .active-link {
        background-color: #005f7f;

        &:hover {
          background-color: #005f7f;
        }
      }
    `,
  ],
})
export class SideNavComponent {
  private router = inject(Router)
  private agencyService = inject(AgencyService)

  // Track current URL reactively
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0])
    )
  )

  // Sidebar menu items with their default tabs
  private allMenuItems: MenuItem[] = [
    {
      icon: 'dashboard',
      label: 'Dashboard',
      link: '/dashboard',
      defaultTab: null,
    },
    {
      icon: 'query_stats',
      label: 'Reports',
      link: '/reports',
      exactMatch: false,
      defaultTab: 'crowd-data',
    },
    {
      icon: 'alt_route',
      label: 'Routes',
      link: '/routes',
      exactMatch: false,
      defaultTab: 'routes',
    },
    { icon: 'route', label: 'Trips', link: '/trips' },
    {
      icon: 'location_on',
      label: 'Waze',
      link: '/waze',
      defaultTab: 'live-data',
      requiresWazeData: true,
    },
    {
      icon: 'settings',
      label: 'Settings',
      link: '/settings',
      defaultTab: null,
    },
  ]

  menuItems = computed(() =>
    this.allMenuItems.filter(
      item => !item.requiresWazeData || this.agencyService.hasWazeData()
    )
  )

  isActiveRoute(link: string): boolean {
    // Use the reactive signal to check the current route
    return this.currentUrl() === link
  }

  navigate(link: string) {
    // Get current query params from the root snapshot
    const currentQueryParams = this.router.routerState.snapshot.root.queryParams
    // Preserve routeid, dates, tagids, routeSearch - but reset tab (page-specific)
    const { tab: _tab, ...paramsToKeep } = currentQueryParams

    // Find the menu item to get the default tab for the target page
    const menuItem = this.menuItems().find(item => item.link === link)
    const queryParams = menuItem?.defaultTab
      ? { ...paramsToKeep, tab: menuItem.defaultTab }
      : paramsToKeep

    this.router.navigate([link], { queryParams })
  }
}

type MenuItem = {
  icon: string
  label: string
  link: string
  defaultTab?: string | null
  exactMatch?: boolean
  requiresWazeData?: boolean
}
